<template>
  <div class="ai-assistant-container">
    <SummaryHeader
      v-if="summaryStatus !== 'initialized' && summaryStatus !== 'interrupted'"
      :initial-context="initialContext"
      :summary-status="summaryStatus"
      @tab-changed="handleTabChanged"
    />

    <div class="content-wrapper">
      <AIAssistantStartTranscription
        v-if="
          (!summaryStatus ||
            summaryStatus == 'recording' ||
            summaryStatus == 'interrupted' ||
            summaryStatus == 'initialized' ||
            summaryStatus == 'processing') &&
            actionState !== 'context'
        "
        :action-state="actionState"
        :summary-status="summaryStatus"
        :generating-transcription="generatingTranscription"
        :initial-context="initialContext"
        :toasts-data="toastsData"
        @update-appointment="onUpdateAppointment"
      />

      <AIAssistantTranscription
        v-if="actionState === 'transcription' && summaryStatus === 'ready'"
        :initial-context="initialContext"
        :transcription-messages="transcriptionMessages"
      />

      <!-- Only show skeleton when processing but NOT when AIAssistantStartTranscription is showing the processing view -->
      <div v-if="generatingTranscription && summaryStatus !== 'processing'" class="skeleton-container">
        <w-skeleton :height="18" shape="default" :width="360" />
        <w-skeleton :height="18" shape="default" :width="360" class="skeleton-item" />
        <w-skeleton :height="18" shape="default" :width="360" class="skeleton-item" />
        <w-skeleton :height="18" shape="default" :width="60" class="skeleton-item" />
      </div>

      <div class="chat-body" v-if="actionState === 'context'">
        <div class="container-summary">
          <p v-html="initialMessage"></p>
        </div>
      </div>

      <div class="chat-body center" v-if="summaryStatus === 'error'">
        <div class="container-summary center">
          <strong>{{ t("aiAssistant.error.title") }}</strong> <span v-text="summaryErrorMessage"></span>
          <br />
          <w-button
            style="display: none"
            @click="restartSession"
            leading-icon="repeat"
            class="restart-button"
            :label="t('aiAssistant.error.restartButton')"
            variant="primary"
          />
        </div>
      </div>

      <SummaryContent
        v-if="summaryStatus === 'ready' && actionState === 'summary'"
        :initial-context="initialContext"
        :action-state="actionState"
        :review-status="reviewStatus"
        :summary-text="summaryText"
        :session-timestamp="sessionTimestamp"
        @update:summary-text="onSummaryTextUpdate"
      />
    </div>

    <SummaryFooter
      v-if="summaryStatus === 'ready' && actionState === 'summary'"
      :summary-status="summaryStatus"
      :action-state="actionState"
      :initial-context="initialContext"
      :custom-forms="customForms"
      :preemium-config="preemiumConfig"
      :summary-text="summaryText"
      :review-status="reviewStatus"
      @summary-saved="onSummarySaved"
      @summary-denied="onSummaryDenied"
      @close="onClose"
    />
  </div>
</template>

<style scoped lang="scss">
.ai-assistant-container {
  --footer-height: 64px;
  display: flex;
  flex-direction: column;
  height: 100%;
  max-height: 100%;
  overflow: hidden;
}

.content-wrapper {
  flex: 1;
  overflow-y: auto;
  min-height: 0;
  margin-bottom: 20px;
}

/* Popover specific styles */
:deep(.popover-body) {
  .ai-assistant-container {
    height: 100%;
    max-height: 100%;
  }
}

.skeleton-container {
  margin: 20px;
}

.skeleton-item {
  margin-top: 15px;
}

.chat-body {
  padding: 16px;
  text-align: left;
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  &.center {
    justify-content: center;
  }
}

.container-summary {
  width: 100%;
  padding-bottom: 15px;

  &.center {
    text-align: center;
  }
}
</style>

<script setup lang="ts">
/**
 * AIAssistantSummary.vue - Summary/Review Component
 *
 * Main component for reviewing transcriptions and summaries.
 * Handles WebSocket updates for status changes and displays
 * appropriate content based on the summary status.
 */
import { ref, watch, inject, onMounted, computed, type Ref } from "vue";
import { marked } from "marked";
import { useI18n } from "vue-i18n";
import SummaryHeader from "./components/SummaryHeader.vue";
import SummaryContent from "./components/SummaryContent.vue";
import SummaryFooter from "./components/SummaryFooter.vue";
import AIAssistantStartTranscription from "./AIAssistantStartTranscription.vue";
import AIAssistantTranscription from "./AIAssistantTranscription.vue";

import { useCustomForms } from "@/composables/useCustomForms";
import { useSessionStore } from "@/composables/useSessionStore";
import { formatSummaryData } from "@/utils/formatters";

const sessionStore = useSessionStore();
const { setRecorderSessionId, setSummary } = sessionStore;

const props = defineProps({
  initialContext: {
    type: Object,
    required: true,
  },
  /**
   * Parent-controlled summary status from the state machine.
   * This takes precedence over initialContext.transcriptionStatus for initial state.
   */
  parentSummaryStatus: {
    type: String,
    default: null,
  },
});

const emit = defineEmits([
  "update-appointment",
  "ready-for-review",
  "review-saved",
  "session-discarded",
  "close",
  "restart-session",
  "update-status",
  "transcription-completed",
  "show-transcription-modal",
]);

const { t } = useI18n();

// State
const actionState = ref("summary");

// Simplified: Use parentSummaryStatus directly from props (controlled by state machine)
// Fall back to computed status from initialContext only if parentSummaryStatus not provided
const summaryStatus = computed(() => {
  if (props.parentSummaryStatus) return props.parentSummaryStatus;
  return getSummaryStatus(props.initialContext?.transcriptionStatus);
});
const initialStatus = summaryStatus.value;

// Initialize summaryText from initialContext if available (for page refresh / session recovery)
const getInitialSummaryText = (): string => {
  const ctx = props.initialContext;
  if (!ctx) return "";

  // Try to get summary data from various possible fields
  const rawSummary =
    ctx.summaryData ||
    ctx.parsedSummary ||
    ctx.reviewedSummary ||
    ctx.data?.summaryData ||
    ctx.data?.parsedSummary ||
    ctx.data?.summary ||
    ctx.summary ||
    ""; // Also check ctx.summary directly

  if (!rawSummary) {
    return "";
  }

  // Format if needed
  return formatSummaryData(rawSummary);
};

const summaryText = ref(initialStatus === "ready" ? getInitialSummaryText() : "");
const reviewStatus = ref(props.initialContext?.reviewedSummary ? "reviewed" : "pending");

// Initialize generatingTranscription based on initial status
// If we're already in processing state on mount, show the loading UI
const generatingTranscription = ref(initialStatus === "processing");
const transcriptionMessages = ref(props.initialContext?.data?.transcription || []);
const initialMessage = ref(props.initialContext?.initialMessage ? marked(props.initialContext.initialMessage) : null);
const summaryErrorMessage = ref("");
const toastsData = ref([]);

// Constants
const today = new Date().toISOString().split("T")[0];
const sessionTimestamp = props.initialContext?.timestamp?.split("T")[0] || today;

const preemiumConfig = {
  active: false,
  mode: "time",
  limit: 30,
  credits: 10,
  releaseDate: "2025-03-24",
};

const isAlfa = props.initialContext.licenseId === 17973 || props.initialContext.licenseId === 9326;

// Composables
const { customForms } = useCustomForms(props.initialContext.forms || []);

// Get transcription modal management methods from parent
const openTranscriptionModal = inject<(iframeUrl: string) => void>("openTranscriptionModal");
const closeTranscriptionModal = inject<() => void>("closeTranscriptionModal");

// Get API client
interface ApiClient {
  get: (url: string, params?: any) => Promise<any>;
  post: (url: string, data?: any) => Promise<any>;
}
const apiClient = inject<ApiClient | null>("apiClient");

// Check if parent has WebSocket connection (AIAssistant.vue)
// If parent is connected, we don't need to connect again
const parentWsConnected = inject<Ref<boolean> | null>("wsConnected", null);

// Status change handling - simplified since parent manages WebSocket
// Local UI state (generatingTranscription) is updated, status changes emitted to parent
// Status change handling - simplified since parent manages WebSocket
// Status tracking is now handled centrally by useStatusTracking composable
const handleStatusChange = (status: string, data: any) => {
  let newSummaryStatus = summaryStatus.value;
  status = status?.toLowerCase();

  console.log("[AIAssistantSummary] handleStatusChange", status, data);

  if (status === "recording") {
    newSummaryStatus = "recording";
    generatingTranscription.value = false; // Not generating yet, still recording
  } else if (["recorded", "transcribing", "transcribed", "analyzing"].includes(status)) {
    newSummaryStatus = "processing";
    // Set generatingTranscription to true to show loading UI during processing states
    generatingTranscription.value = true;
  } else if (status === "summarized") {
    newSummaryStatus = "ready";
    transcriptionMessages.value = data.transcription;

    if (!data.reviewedSummary) {
      reviewStatus.value = "pending";
      summaryText.value = data.parsedSummary;
    } else {
      summaryText.value = data.reviewedSummary;
      reviewStatus.value = "reviewed";
    }

    generatingTranscription.value = false;

    // Close iframe modal and open transcription text modal when status is Summarized
    if (closeTranscriptionModal) {
      closeTranscriptionModal();
    }

    // Fetch transcription text from API
    const sessionId = props.initialContext?.sessionId || props.initialContext?.recorderSessionId;
    if (apiClient && sessionId) {
      apiClient
        .get(`/session/${sessionId}`)
        .then((sessionData) => {
          // Extract transcription text and summary data from session data
          const transcriptionText = sessionData?.transcription || sessionData?.data?.transcription || "";

          // Try multiple possible field names for summaryData
          const summaryData =
            sessionData?.summaryData ||
            sessionData?.data?.summaryData ||
            sessionData?.summary ||
            sessionData?.data?.summary ||
            sessionData?.parsedSummary ||
            sessionData?.data?.parsedSummary ||
            sessionData?.reviewedSummary ||
            sessionData?.data?.reviewedSummary ||
            "";

          // Set summaryText with summaryData if available
          if (summaryData) {
            const formattedSummary = formatSummaryData(summaryData);
            setRecorderSessionId(sessionId);
            setSummary(formattedSummary);
            summaryText.value = formattedSummary;
          }

          emit("show-transcription-modal", {
            transcription: transcriptionText,
            summary: data.parsedSummary || data.reviewedSummary || sessionData?.summary,
            summaryData: summaryData,
          });
        })
        .catch((error) => {
          console.error("[AIAssistantSummary] Error fetching session data:", error);
          // Fallback: use data from WebSocket
          emit("show-transcription-modal", {
            transcription: data.transcription,
            summary: data.parsedSummary || data.reviewedSummary,
            summaryData: data.parsedSummary || data.reviewedSummary || "",
          });
        });
    } else {
      // Fallback: use data from WebSocket
      emit("show-transcription-modal", {
        transcription: data.transcription,
        summary: data.parsedSummary || data.reviewedSummary,
        summaryData: data.parsedSummary || data.reviewedSummary || "",
      });
    }

    if (reviewStatus.value === "pending") {
      emit("ready-for-review");
    }
  }

  // Emit status update to parent (state machine handles actual status changes)
  if (newSummaryStatus && summaryStatus.value !== "error") {
    emit("update-status", newSummaryStatus);
  }
};

// WebSocket is managed by parent (AIAssistant.vue)
// Status changes come through parentSummaryStatus prop

onMounted(() => {
  // Component mounted - initialization complete
});

// Watch summaryStatus to sync generatingTranscription
// This ensures the processing UI shows when status changes from external sources (parent, props)
watch(
  summaryStatus,
  (newStatus) => {
    // Set generatingTranscription based on status
    if (newStatus === "processing") {
      generatingTranscription.value = true;
    } else if (newStatus === "ready" || newStatus === "error" || newStatus === "interrupted") {
      generatingTranscription.value = false;
    }
  },
  { immediate: false } // Don't run immediately since we set initial value above
);

// Watch parentSummaryStatus prop for changes from parent state machine
// This ensures the UI reflects the state machine status (e.g., "processing" when reviewStatus is "processing")
// immediate: true ensures this fires on mount if the prop has a value
watch(
  () => props.parentSummaryStatus,
  (newParentStatus) => {
    // summaryStatus is now computed from props, just update local UI state
    if (newParentStatus === "processing") {
      generatingTranscription.value = true;
    } else if (newParentStatus === "ready" || newParentStatus === "error") {
      generatingTranscription.value = false;

      // When status changes to "ready", update summaryText if we have summary data
      if (newParentStatus === "ready") {
        const newSummary = getInitialSummaryText();
        if (newSummary && !summaryText.value) {
          summaryText.value = newSummary;
        }
      }
    }
  },
  { immediate: true }
);

// Watch initialContext.summaryData for changes - this handles when summary data arrives after component mount
// This is critical for the flow: processing -> review_ready (summaryData arrives)
watch(
  () => props.initialContext?.summaryData,
  (newSummaryData, oldSummaryData) => {
    if (newSummaryData && newSummaryData !== oldSummaryData) {
      // Only update if we don't already have summary text (avoid overwriting user edits)
      if (!summaryText.value) {
        const newSummary = getInitialSummaryText();
        if (newSummary) {
          summaryText.value = newSummary;
        }
      }
    }
  },
  { immediate: true, deep: false }
);

// Watch for external status changes from props - just emit update to parent
watch(
  () => props.initialContext?.transcriptionStatus || props.initialContext?.recorderStatus,
  (newStatus) => {
    if (newStatus) {
      const mappedStatus = getSummaryStatus(newStatus);
      if (mappedStatus && mappedStatus !== summaryStatus.value) {
        emit("update-status", mappedStatus);
      }
    }
  }
);

// WebSocket is managed by parent (AIAssistant.vue)
// Status changes come through props.parentSummaryStatus

// Methods
function getSummaryStatus(status: string) {
  status = status?.toLowerCase();
  return ["recorded", "transcribing", "transcribed", "analyzing"].includes(status) ? "processing" : status;
}

const handleTabChanged = (tab: string) => {
  actionState.value = tab;
};

const onUpdateAppointment = (appointment: any) => {
  emit("update-appointment", {
    appointmentId: appointment.appointmentId,
  });
};

const onSummarySaved = () => {
  reviewStatus.value = "reviewed";
  emit("review-saved");
};

const onSummaryDenied = () => {
  emit("session-discarded");
};

const onClose = () => {
  emit("close");
};

const restartSession = () => {
  // Reset local state and emit to parent (summaryStatus controlled by props)
  actionState.value = "summary";
  generatingTranscription.value = true;
  initialMessage.value = null;
  summaryText.value = null;
  reviewStatus.value = "pending";
  summaryErrorMessage.value = "";
  toastsData.value = [];
  emit("update-status", "initialized");
  emit("restart-session", { initialContext: props.initialContext });
};

const onSummaryTextUpdate = (newValue: string) => {
  summaryText.value = newValue;
};

if (props.initialContext.data) {
  handleStatusChange(summaryStatus.value, props.initialContext.data);
}
</script>
